import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { map, shareReplay, take, tap } from 'rxjs/operators';
import { UploadFile, UploadInput } from 'ngx-uploader-esb';
import moment from 'moment';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';

import { AppConstants } from '../../../app.constants';
import { AppConfig, WINDOW } from '../../../app.settings';
import { CustomFileModel } from '../../models/entities/file.type';
import { AuthDataInterface } from '../../models/view-models/account-models/auth-data.interface';
import { PlaylistItemDTO } from '../../models/view-models/playlist-item-models/playlist-item-dto.model';
import { PlaylistItemType } from '../../models/enums/playlist-item-type.enum';
import { PlaylistItemLinkModel } from '../../models/view-models/playlist-item-models/playlist-item-link.model';

@Injectable()
export class FilesService {

  endpoint: string;
  uploadInput$: BehaviorSubject<UploadInput> = new BehaviorSubject<UploadInput>(null);
  playlistItemsTypes = PlaylistItemType;
  downloadPreviewRequest: Subscription;

  #window = inject(WINDOW);

  constructor(public http: HttpClient, public config: AppConfig) {
    this.endpoint = this.config.getConfig('api') + 'File';
  }

  convertPresentationToImages(fileId: string, isToConvertToWebm: boolean): Observable<CustomFileModel[]> {
    const params: HttpParams = new HttpParams()
      .set('fileId', fileId)
      .set('convertToWebm', isToConvertToWebm);

    return this.http.post<CustomFileModel[]>(`${this.endpoint}/ConvertPresentationToImages`, null, { params });
  }

  convertMP4ToWebm(fileIds: string[]): Observable<CustomFileModel[]> {
    return this.http.post<CustomFileModel[]>(`${this.endpoint}/ConvertMP4ToWebm`, fileIds);
  }

  convertPortraitToLandscape(fileIds: string[]): Observable<CustomFileModel[]> {
    return this.http.post<CustomFileModel[]>(`${this.endpoint}/ConvertPortraitToLandscape`, fileIds);
  }

  getRecentFiles(count?: number): Observable<CustomFileModel[]> {
    const urlFull = `${this.endpoint}/GetRecentFiles?Count=${count || 10}`;

    return this.http.get<CustomFileModel[]>(urlFull, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }

  downloadFile(id: string, isImagePreview = false): Observable<Blob> {
    const urlFull = this.endpoint + '/GetFile/' + id;
    const params = new HttpParams()
     .set('isImagePreview', isImagePreview);

    return this.http.get<Blob>(urlFull, { responseType: 'blob' as 'json', params });
  }

  downloadAgent(): Observable<Blob> {
    return this.http.get<Blob>(`${this.endpoint}/DownloadAgent`, { responseType: 'blob' as 'json' });
  }

  getRecentAndPlaylistItemFiles(playlistItemFiles: CustomFileModel[]): Observable<CustomFileModel[]> {
    return this.getRecentFiles()
      .pipe(
        tap((files) => this.addMissingFileIds(files, playlistItemFiles)),
        shareReplay(1)
      );
  }

  addFileToTheRecentFilesList(recentFiles: Observable<CustomFileModel[]>, file: CustomFileModel): Observable<CustomFileModel[]> {
    return recentFiles.pipe(
      map((files) => [...files, file])
    );
  }

  addMissingFileIds(recentFiles: CustomFileModel[], playlistItemFiles: CustomFileModel[]): void {
    playlistItemFiles?.forEach((file) => {
      if (file?.Id && !recentFiles.find(recentFile => recentFile.Id === file.Id)) {
        recentFiles.push(file);
      }
    });
  }

  startUpload(uploadedFiles: UploadFile[]): void {
    uploadedFiles.forEach(uploadedFile => {
      const fileType = uploadedFile.nativeFile['type'];

      if (fileType.includes(AppConstants.WMV_FILE_TYPE)) {
        this.fileHandler(uploadedFile);
      } else if (fileType.includes(AppConstants.AUDIO_FILE_TYPE) || fileType.includes(AppConstants.VIDEO_FILE_TYPE)) {
        this.setFileDuration(uploadedFile);
      } else {
        this.emitUpload(uploadedFile, null);
      }
    });
  }

  fileHandler(uploadedFile: UploadFile): void {
    const reader = new FileReader();
    const file = uploadedFile.nativeFile;

    reader.onloadend = (event: ProgressEvent) => {
      const target = event.target as FileReader;

      if (target.readyState === AppConstants.FILE_READER_SUCCESS_STATE) {
        this.emitUpload(uploadedFile, null);
      }
    };

    reader.readAsDataURL(file);
  }

  setFileDuration(uploadedFile: UploadFile): void {
    const urlSt = URL.createObjectURL(uploadedFile.nativeFile);
    const audio = new Audio(urlSt);

    if (audio && audio.src) {
      const fileType: string = uploadedFile.nativeFile.type;
      const eventToListen: string = fileType.endsWith(AppConstants.AVI_FILE_TYPE) ?
        AppConstants.LOAD_START_EVENT :
        AppConstants.LOADED_METADATA_EVENT;

      audio.addEventListener(eventToListen, () => {
        const audioFileDuration = moment('2015-01-01')
          .startOf('day')
          .seconds(audio.duration || AppConstants.DEFAULT_PLAYLIST_ITEM_DURATION_IN_SECONDS)
          .format(AppConstants.AUDIO_FILE_DATE_TIME_FORMAT);

        this.emitUpload(uploadedFile, audioFileDuration);
      });
    }
  }

  emitUpload(uploadedFile: UploadFile, duration: string): void {
    const authData: AuthDataInterface = JSON.parse(localStorage.getItem(AppConstants.AUTH_DATA_NAME));

    const event: UploadInput =  {
      type: 'uploadFile',
      url: `${this.endpoint}/UploadFile`,
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authData.userToken}`
      },
      file: uploadedFile,
      data: {
        AudioFileDuration: duration
      }
    };

    this.uploadInput$.next(event);
  }

  isDirectory(path: string): Observable<boolean> {
    const params: HttpParams = new HttpParams().set('path', path);
    return this.http.get<boolean>(`${this.endpoint}/IsDirectory`, { params });
  }

  shouldConvertPortraitToLandscape(item: PlaylistItemDTO): boolean {
    const file = item.PlaylistItemLinks[0].File;
    return this.getFileType(item)?.includes(AppConstants.MP4_FILE_TYPE) && file.Height > file.Width;
  }

  getFileType(item: PlaylistItemDTO, index = 0): string {
    return item.PlaylistItemLinks[index].File?.FileName.split(AppConstants.FILE_EXTENSION_DELIMITER).pop().toLowerCase();
  }

  generateItem(files: CustomFileModel[], playlistItems: PlaylistItemDTO[], shouldDownloadPreview = true) {
    files.forEach((file, i) => {
      const addedItem = new PlaylistItemDTO();
      addedItem.PlaylistItemTitle = file.FileName;
      addedItem.PlaylistFileName = file.FileName;
      addedItem.percentage = AppConstants.FULLY_DOWNLOADED_FILE;
      addedItem.PlaylistItemType = PlaylistItemType.RecentFile;

      addedItem.PlaylistItemLinks.push(new PlaylistItemLinkModel());
      addedItem.PlaylistItemLinks[0].FileId = file.Id;
      addedItem.PlaylistItemLinks[0].File = file;

      if (file.Duration) {
        const roundedDuration = this.roundDuration(file.Duration);
        addedItem.ExactVideoDuration = roundedDuration;
        addedItem.Duration = roundedDuration;
        addedItem.PlaylistItemLinks[0].duration = roundedDuration;
        addedItem.useExactDuration = true;
      }

      this.setIcon(addedItem);

      if (shouldDownloadPreview) {
        this.previewContent(addedItem);
      }

      playlistItems.push(addedItem);
    });
  }

  setIcon(item: PlaylistItemDTO, isAndroid: boolean = false) {
    switch (item.PlaylistItemType) {
      case PlaylistItemType.Url:
        if (isAndroid) {
          item.PlaylistItemLinks[0].icon = AppConstants.FILE_TYPES_ICONS_PATH + 'www.png';
        }
        break;
      case PlaylistItemType.RecentFile:
      case PlaylistItemType.UploadFile:
        this.setFileIcon(item.PlaylistItemLinks[0]);
        break;
    }
  }

  previewContent(item: PlaylistItemDTO, popover: NgbPopover = null, isVideoPreview?: boolean): number | undefined {
    if (popover && item.PlaylistItemType !== this.playlistItemsTypes.RecentFile &&
      item.PlaylistItemType !== this.playlistItemsTypes.UploadFile) {
      return;
    }

    const itemLink = item.PlaylistItemLinks[0];

    if (AppConstants.CONTENT_PREVIEW_FILE_TYPES_REGULAR.test(itemLink.File.Title)) {
      const isImagePreview = AppConstants.CONTENT_PREVIEW_VIDEO_FILE_TYPES_REGULAR.test(itemLink.File.Title) && !isVideoPreview;

      if (!itemLink.content || isVideoPreview) {
        this.downloadPreview(itemLink, popover, isImagePreview, isVideoPreview);
      }
      else if (itemLink.content && popover) {
       return this.openPopover(popover);
      }
    }
  }

  downloadPreview(itemLink: PlaylistItemLinkModel, popover: NgbPopover = null, isImagePreview: boolean, isVideoPreview: boolean) {
    this.downloadPreviewRequest = this.downloadFile(itemLink.FileId, isImagePreview)
      .pipe(take(1))
      .subscribe(file => {
        if (file) {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            isVideoPreview ?
              itemLink.videoContent = reader.result :
                itemLink.content = reader.result;
          }
          reader.onloadend = () => {
            if (popover) {
              return this.openPopover(popover);
            }
          }
        }
      });
  }

  roundDuration(duration: string): string {
    let [hours, minutes, seconds] = duration.split(AppConstants.PLAYLIST_ITEM_DELIMITER);
    seconds = Math.ceil(+seconds).toString();
    return `${ hours }:${ minutes }:${ seconds }`;
  }

  private openPopover(popover: NgbPopover): number {
    popover.open();

    return this.#window.setTimeout(() => {
      popover.close();
    }, AppConstants.CONTENT_PREVIEW_TIMEOUT);
  }

  private setFileIcon(itemLink: PlaylistItemLinkModel) {
    const fileExtension = itemLink.File?.Title.split('.').pop().toLowerCase();

    switch(fileExtension) {
      case AppConstants.JPG_FILE_TYPE:
      case AppConstants.PNG_FILE_TYPE:
      case AppConstants.WEBP_FILE_TYPE:
        itemLink.icon = AppConstants.FILE_TYPES_ICONS_PATH + 'image-logo.png';
        break;

      case AppConstants.MP4_FILE_TYPE:
      case AppConstants.WMV_FILE_TYPE:
      case AppConstants.AVI_FILE_TYPE:
      case AppConstants.WEBM_FILE_TYPE:
        itemLink.icon = AppConstants.FILE_TYPES_ICONS_PATH + 'video-logo.png';
        break;

      case AppConstants.SVG_FILE_TYPE:
        itemLink.icon = AppConstants.FILE_TYPES_ICONS_PATH + 'svg-logo.png';
        break;

      case AppConstants.PDF_FILE_TYPE:
        itemLink.icon = AppConstants.FILE_TYPES_ICONS_PATH + 'pdf-logo.png';
        break;

      case AppConstants.PPT_FILE_TYPE:
      case AppConstants.PPTX_FILE_TYPE:
        itemLink.icon = AppConstants.FILE_TYPES_ICONS_PATH + 'ppt-logo.png';
        break;

      case AppConstants.DOCX_FILE_TYPE:
        itemLink.icon = AppConstants.FILE_TYPES_ICONS_PATH + 'word-logo.png';
        break;

      case AppConstants.XLSX_FILE_TYPE:
        itemLink.icon = AppConstants.FILE_TYPES_ICONS_PATH + 'excel-logo.png';
        break;

      default:
        itemLink.icon = AppConstants.PLAYLIST_ITEM_ICONS_PATH + 'file.png';
    }
  }
}
